// @file Opens native share menu
import { trackEvent } from '@@/bits/analytics'
import { browserCan } from '@@/bits/browser'
import { captureException } from '@@/bits/error_tracker'
import { buildSearchString, buildUrlFromPath } from '@@/bits/location'

const openNativeShare = async (data: ShareData): Promise<void> => {
  const canUseNativeShare = browserCan('share')
  if (!canUseNativeShare) {
    captureException(new Error('Attempted to call share when not available'))
    return
  }

  try {
    await window.navigator.share(data)
  } catch (error) {
    // We don't capture AbortError when the user canceled the share operation or there are no share targets available
    // @see https://developer.mozilla.org/en-US/docs/Web/API/Navigator/share#exceptions
    const ABORT_ERROR_CODE = 20
    if (error.code !== ABORT_ERROR_CODE) {
      captureException(error)
    }
  }
}

type ShareService =
  | 'facebook'
  | 'twitter'
  | 'email'
  | 'skoletube'
  | 'googleClassroom'
  | 'microsoftTeams'
  | 'whatsapp'
  | 'line'
const shareLinkOnService = (link: string, service: ShareService): void => {
  const openShareUrl = window.open

  trackEvent(
    'Share Menu',
    {
      facebook: 'Clicked Facebook',
      twitter: 'Clicked Twitter',
      email: 'Clicked email',
      skoletube: 'Clicked Skoletube',
      googleClassroom: 'Clicked Google Classroom',
      microsoftTeams: 'Clicked Microsoft Teams',
      whatsapp: 'Clicked WhatsApp',
      line: 'Clicked Line',
      zoom: 'Clicked Zoom',
    }[service],
  )

  switch (service) {
    case 'email':
      window.location.href = link
      break
    case 'googleClassroom':
      openShareUrl(link, service, 'width=640,height=400')
      break
    default:
      openShareUrl(link)
      break
  }
}

interface BuildShareLinkParameters {
  service: ShareService
  url: string
  title: string
  description: string
  media: string
  publicKey: string
  params?: string // this is not used in email
}

const buildShareLink = ({
  service,
  url,
  title,
  description,
  media,
  publicKey,
  params,
}: BuildShareLinkParameters): string => {
  switch (service) {
    case 'email':
      return `mailto:?${buildSearchString({ subject: title, body: description })}`
    case 'googleClassroom':
      return buildUrlFromPath(
        `/padlets/${publicKey}/shares/google_classroom`,
        params !== undefined ? { searchParams: Object.fromEntries(new URLSearchParams(params)) } : undefined,
      )
    case 'microsoftTeams':
      return `https://teams.microsoft.com/share?${buildSearchString({ href: url, msgText: title })}`
    case 'whatsapp':
      return `https://wa.me/?${buildSearchString({ text: url })}`
    case 'line':
      return `https://social-plugins.line.me/lineit/share?${buildSearchString({ url })}`
    default:
      return buildUrlFromPath(`/media/share/${service}`, {
        searchParams: {
          url,
          title,
          description,
          media,
          ...(params !== undefined ? { params } : {}),
        },
      })
  }
}
export { buildShareLink, openNativeShare, shareLinkOnService }
export type { BuildShareLinkParameters, ShareService }
